import { useTranslation } from 'react-i18next'
import { mdiMagnify } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'
import { Checkbox } from '../ui/checkbox'
import { RULE_CLASSES, UNIVERSES, type RuleClassFilter, type RuleFilters, type Universe } from './rule-catalog'

/**
 * The filter bar of the rules page (spec 2026-10-27-21 § 3): the kind of rule,
 * the universe (with "Spécifiques seulement") and a search on label and code.
 * The page keeps the filters in the URL, so a link reopens the same view.
 */
export function RulesFilterBar({
    filters,
    onChange,
}: {
    filters: RuleFilters
    onChange: (next: RuleFilters) => void
}) {
    const { t } = useTranslation('compliance')
    const classes: RuleClassFilter[] = ['all', ...RULE_CLASSES]
    const pill = (active: boolean) =>
        `rounded-full border px-3 py-1 text-xs transition-colors ${
            active ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-foreground/80 hover:bg-muted'
        }`

    return (
        <div className="mb-5 space-y-3 rounded-lg border bg-muted/20 p-3" data-testid="business-rules-filters">
            <div className="flex flex-wrap items-center gap-2" role="group" data-testid="business-rules-filter-class">
                {classes.map((c) => (
                    <button
                        key={c}
                        type="button"
                        className={pill(filters.klass === c)}
                        aria-pressed={filters.klass === c}
                        onClick={() => onChange({ ...filters, klass: c })}
                        data-testid={`business-rules-filter-class-${c}`}
                    >
                        {t(`business_rules.class.${c}`)}
                    </button>
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    className={pill(filters.universe === null)}
                    aria-pressed={filters.universe === null}
                    onClick={() => onChange({ ...filters, universe: null, strict: false })}
                    data-testid="business-rules-filter-universe-all"
                >
                    {t('business_rules.universe.all')}
                </button>
                {UNIVERSES.map((u: Universe) => (
                    <button
                        key={u}
                        type="button"
                        className={pill(filters.universe === u)}
                        aria-pressed={filters.universe === u}
                        onClick={() => onChange({ ...filters, universe: u })}
                        data-testid={`business-rules-filter-universe-${u}`}
                    >
                        {t(`business_rules.universe.${u}`)}
                    </button>
                ))}
                {filters.universe && (
                    <label className="ml-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Checkbox
                            checked={filters.strict}
                            onCheckedChange={(v) => onChange({ ...filters, strict: v === true })}
                            data-testid="business-rules-filter-strict"
                        />
                        {t('business_rules.universe.strict')}
                    </label>
                )}
                <div className="relative ml-auto w-full sm:w-64">
                    <AppIcon path={mdiMagnify} size="sm" className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={filters.q}
                        onChange={(e) => onChange({ ...filters, q: e.target.value })}
                        placeholder={t('business_rules.search')}
                        className="h-8 pl-8"
                        data-testid="business-rules-search"
                    />
                </div>
            </div>
        </div>
    )
}
